import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { moduleHref } from '../../module';
import { Loading, ErrorState, Empty } from '../../components/ui';
import { Kpis, Panel, BarsH, Stack100 } from '../../components/charts';

/* ============================================================
   Dashboard cx.

   Pertanyaan yang dijawab halaman ini bukan "berapa persen
   commissioning selesai" melainkan "peralatan mana yang sudah
   terbukti bekerja, dan mana yang tertahan".

   Angka terpenting di sini adalah LULUS TANPA LAPORAN, dan ia
   seharusnya selalu nol: status tidak berpindah tanpa berkas.
   Kalau ia bukan nol, ada peralatan yang statusnya hijau dan
   buktinya tidak ada — dan itu baru ketahuan pada hari serah
   terima, ketika ruangnya sudah tertutup.

   Setiap angka yang mengejutkan di halaman ini punya jalan ke
   barisnya. Dasbor yang hanya menyebut "12 tertahan" menyuruh
   orang menebak yang mana.
   ============================================================ */

export const CX_LEVELS = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;

/**
 * Nama tingkat sengaja TIDAK diterjemahkan — cermin dari
 * commissioning-core.ts. Nama inilah yang tercetak di sertifikat uji,
 * di WBS, dan di judul berkas laporan; menerjemahkannya di layar
 * membuat orang mencari baris yang tidak ada di dokumen mana pun.
 */
export const LEVEL_NAME: Record<string, string> = {
  L1: 'FAT / Factory Witness',
  L2: 'QA/QC Installation & Dead Test',
  L3: 'Pre-Functional Test',
  L4: 'Functional Test',
  L5: 'Integrated System Test',
};

export const RESULT_LABEL: Record<string, string> = {
  NOT_STARTED: 'Belum mulai',
  IN_PROGRESS: 'Berjalan',
  PASSED: 'Lulus',
  FAILED: 'Gagal',
  PASSED_WITH_PUNCH: 'Lulus dengan punch',
};

/**
 * Lulus dengan punch tetap hijau, sama seperti lulus bersih.
 * Peralatannya diterima dan sisa butirnya dijadwalkan; memberinya
 * warna peringatan akan membuat orang berhenti memakainya dan
 * kembali menandai PASSED dengan daftar punch di kepala sendiri.
 * Sisa pekerjaannya dibawa kolom punch, bukan warna.
 */
export const RESULT_CHIP: Record<string, string> = {
  NOT_STARTED: '',
  IN_PROGRESS: 'blue',
  PASSED: 'good',
  FAILED: 'crit',
  PASSED_WITH_PUNCH: 'good',
};

const BLOCK_REASON: Record<string, string> = {
  PREVIOUS_LEVEL_NOT_PASSED: 'Tingkat sebelumnya belum lulus seluruhnya',
  OPEN_NCR: 'NCR masih terbuka',
  NO_REPORT: 'Tingkat sebelumnya lulus tanpa berkas laporan',
};

const UNKNOWN_FLOOR = 'Tidak diketahui';

export function fmtInt(n: number): string {
  return n.toLocaleString('id-ID');
}

export function floorLabel(floor: string | null): string {
  return floor === null || floor === '' || floor === 'UNKNOWN' ? UNKNOWN_FLOOR : floor;
}

export type Summary = {
  equipment: number;
  items: number;
  byLevel: { level: string; name: string; passed: number; total: number; pct: number }[];
  byFloor: { floor: string; passed: number; total: number; pct: number }[];
  outcomes: {
    PASSED: number;
    PASSED_WITH_PUNCH: number;
    FAILED: number;
    IN_PROGRESS: number;
    NOT_STARTED: number;
  };
  readyForNext: number;
  blocked: number;
  withoutReport: number;
  openNcrs: number;
};

type Blocked = {
  equipment: {
    equipmentId: string;
    tag: string;
    type: string | null;
    floor: string | null;
    room: string | null;
    reason: string;
    blockingLevel: string | null;
    blockingNcrs: { id: string; ncrNo: string }[];
  }[];
  totals: { blocked: number; readyForNext: number };
};

/** Baris yang mengaku lulus dan tidak bisa dibuktikan. */
const UNPROVEN = '/levels?results=PASSED,PASSED_WITH_PUNCH&hasReport=0';

export function Dashboard({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [data, setData] = useState<Summary | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [summary, held] = await Promise.all([
        api.get<Summary>(`/cx/summary?projectId=${projectId}`),
        api.get<Blocked>(`/cx/blocked?projectId=${projectId}`),
      ]);
      setData(summary);
      setBlocked(held);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ringkasan commissioning tidak bisa dimuat.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const o = data.outcomes;
  const tested = data.items - o.NOT_STARTED;

  // Tingkat tanpa satu pun baris uji dikeluarkan dari grafik. Persen
  // nol atas nol bukan "belum ada yang lulus", ia "tidak berlaku pada
  // proyek ini" — dan batang nol akan dibaca sebagai yang pertama.
  const levelsWithRows = data.byLevel.filter((l) => l.total > 0);
  const levelsWithout = data.byLevel.filter((l) => l.total === 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Commissioning</h1>
          <p>
            Terpasang bukan berarti bekerja. Setiap peralatan dibuktikan tingkat demi tingkat,
            berurutan, dan tidak pernah atas perkataan orang yang memasangnya.
          </p>
        </div>
      </div>

      {data.equipment === 0 ? (
        <Empty
          title="Belum ada peralatan terdaftar"
          body="Register peralatan datang dari programme dan dari daftar pengadaan. Sebelum ada peralatan, tidak ada yang bisa diuji."
        />
      ) : (
        <>
          <Kpis
            items={[
              { label: 'Peralatan', value: fmtInt(data.equipment), note: `${fmtInt(data.items)} butir uji` },
              {
                label: 'Sudah diuji',
                value: fmtInt(tested),
                note:
                  tested === 0
                    ? 'commissioning belum dimulai'
                    : `${fmtInt(o.PASSED + o.PASSED_WITH_PUNCH)} lulus, ${fmtInt(o.FAILED)} gagal`,
              },
              {
                label: 'Siap naik tingkat',
                value: fmtInt(data.readyForNext),
                note: 'boleh diuji sekarang juga',
              },
              {
                label: 'Tertahan',
                value: fmtInt(data.blocked),
                note: data.openNcrs > 0 ? `${fmtInt(data.openNcrs)} NCR terbuka` : 'tidak ada NCR terbuka',
                tone: data.blocked > 0 ? 'crit' : 'good',
              },
              {
                label: 'Lulus tanpa laporan',
                value: fmtInt(data.withoutReport),
                note: data.withoutReport > 0 ? 'kelulusan tanpa bukti' : 'seperti seharusnya',
                tone: data.withoutReport > 0 ? 'crit' : 'good',
              },
            ]}
          />

          {data.withoutReport > 0 && (
            <div className="banner err" role="alert">
              {fmtInt(data.withoutReport)} baris tercatat lulus tanpa berkas laporan uji. Angka ini
              seharusnya nol — status tidak berpindah tanpa berkas, jadi baris ini masuk lewat impor
              atau lewat terobosan.{' '}
              <button className="btn btn-ghost btn-sm" onClick={() => nav(UNPROVEN)}>
                Lihat barisnya
              </button>
            </div>
          )}

          <div className="grid-2">
            <Panel
              title="Kemajuan per tingkat"
              note="Yang dihitung lulus adalah baris yang lulus, termasuk lulus dengan punch."
            >
              {levelsWithRows.length === 0 ? (
                <p className="hint">Belum ada satu pun baris uji di proyek ini.</p>
              ) : (
                <BarsH
                  rows={levelsWithRows.map((l) => ({
                    label: `${l.level} · ${l.name}`,
                    value: l.pct,
                    tone: l.pct >= 100 ? 'good' : 'accent',
                  }))}
                  suffix="%"
                />
              )}
              <p className="panel-foot">
                {levelsWithRows.map((l) => `${l.level} ${fmtInt(l.passed)}/${fmtInt(l.total)}`).join(' · ')}
                {levelsWithout.length > 0 && (
                  <>
                    {levelsWithRows.length > 0 && ' · '}
                    {levelsWithout.map((l) => l.level).join(', ')} belum punya satu pun baris uji —
                    bukan nol persen, melainkan belum ada.
                  </>
                )}
              </p>
            </Panel>

            <Panel
              title="Hasil uji"
              note="Belum mulai bukan kegagalan. Di proyek yang belum sampai commissioning, itu memang seluruh isinya."
            >
              {data.items === 0 ? (
                <p className="hint">Belum ada butir uji yang dibuat.</p>
              ) : (
                <Stack100
                  slices={[
                    { name: 'Lulus', value: o.PASSED, color: 'var(--series-3)' },
                    { name: 'Lulus dengan punch', value: o.PASSED_WITH_PUNCH, color: 'var(--series-2)' },
                    { name: 'Gagal', value: o.FAILED, color: 'var(--status-critical)' },
                    { name: 'Berjalan', value: o.IN_PROGRESS, color: 'var(--accent)' },
                    { name: 'Belum mulai', value: o.NOT_STARTED, color: 'var(--text-muted)' },
                  ]}
                />
              )}
            </Panel>
          </div>

          {data.byFloor.length > 0 && (
            <Panel
              title="Kemajuan per lantai"
              note="Lantai adalah cara serah terima dilakukan, jadi lantai adalah cara kemajuan dibaca."
              wide
            >
              <BarsH
                rows={data.byFloor.map((f) => ({
                  label: `${floorLabel(f.floor)} · ${fmtInt(f.passed)}/${fmtInt(f.total)}`,
                  value: f.pct,
                  tone: f.pct >= 100 ? 'good' : 'accent',
                }))}
                suffix="%"
              />
            </Panel>
          )}

          <Panel
            title="Peralatan tertahan"
            note={
              blocked === null
                ? 'Daftar peralatan tertahan tidak bisa dimuat.'
                : blocked.equipment.length === 0
                  ? 'Tidak ada peralatan yang tertahan gerbang.'
                  : 'Tertahan berarti tingkat berikutnya tidak boleh dimulai sampai penyebabnya ditutup.'
            }
            wide
          >
            {blocked === null ? (
              <p className="hint">Coba muat ulang halaman.</p>
            ) : blocked.equipment.length === 0 ? (
              <p className="hint">
                {fmtInt(blocked.totals.readyForNext)} peralatan siap diuji ke tingkat berikutnya.
              </p>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Jenis</th>
                      <th>Lantai</th>
                      <th>Ruang</th>
                      <th>Tertahan oleh</th>
                      <th>Di tingkat</th>
                      <th>NCR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blocked.equipment.map((e) => (
                      <tr
                        key={e.equipmentId}
                        onClick={() => nav(`/levels?search=${encodeURIComponent(e.tag)}`)}
                        tabIndex={0}
                        onKeyDown={(ev) =>
                          ev.key === 'Enter' && nav(`/levels?search=${encodeURIComponent(e.tag)}`)
                        }
                      >
                        <td className="num">{e.tag}</td>
                        <td>{e.type ?? '—'}</td>
                        <td>{floorLabel(e.floor)}</td>
                        <td>{e.room ?? '—'}</td>
                        <td>{BLOCK_REASON[e.reason] ?? e.reason}</td>
                        <td>
                          {e.blockingLevel ? (
                            <span title={LEVEL_NAME[e.blockingLevel] ?? e.blockingLevel}>
                              {e.blockingLevel}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {e.blockingNcrs.length === 0
                            ? '—'
                            : e.blockingNcrs.map((n) => (
                                <a
                                  key={n.id}
                                  className="chip crit"
                                  style={{ textDecoration: 'none', marginRight: 6 }}
                                  href={moduleHref('QM', `/ncrs/${n.id}`)}
                                  onClick={(ev) => ev.stopPropagation()}
                                >
                                  {n.ncrNo}
                                </a>
                              ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </>
  );
}
